import { StateCreator } from 'zustand';
import { api } from '../../api/client';

export interface Client {
  id: string;
  _id?: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
  _optimistic?: boolean;
  [key: string]: any; // Динамические поля
}

export interface ClientsPagination {
  page: number;
  totalPages: number;
  total: number;
  perPage: number;
}

export interface ClientsFilters {
  search?: string;
  name?: string;
  email?: string;
  phone?: string;
}

export interface ClientsSlice {
  clients: Client[];
  clientsLoading: boolean;
  clientsError: string | null;
  clientsPagination: ClientsPagination;
  clientsFilters: ClientsFilters;
  optimisticClients: Map<string, Client>;

  fetchClients: (filters?: ClientsFilters) => Promise<void>;
  createClient: (data: Partial<Client>) => Promise<Client>;
  updateClient: (id: string, data: Partial<Client>) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;

  handleClientSSE: (event: any) => void;

  setClientsFilters: (filters: ClientsFilters) => void;
  setClientsPage: (page: number) => void;

  optimisticCreateClient: (tempId: string, data: Partial<Client>) => void;
  optimisticUpdateClient: (id: string, data: Partial<Client>) => void;
  optimisticDeleteClient: (id: string) => void;
}

export const createClientsSlice: StateCreator<ClientsSlice> = (set, get) => ({
  clients: [],
  clientsLoading: false,
  clientsError: null,
  clientsPagination: {
    page: 1,
    totalPages: 1,
    total: 0,
    perPage: 20,
  },
  clientsFilters: {
    search: '',
  },
  optimisticClients: new Map(),

  fetchClients: async (filters?: ClientsFilters) => {
    const currentFilters = filters || get().clientsFilters;

    set({ clientsLoading: true, clientsError: null });

    try {
      const params = {
        page: get().clientsPagination.page,
        per_page: get().clientsPagination.perPage,
        ...currentFilters,
      };

      const response = await api.get('/clients', { params });

      // API client подменяет response.data на массив и сохраняет пагинацию в response.pagination
      const clients = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      const pagination = response.pagination || response.data || {};

      set({
        clients,
        clientsPagination: {
          page: pagination.page || 1,
          totalPages: pagination.pages || pagination.total_pages || 1,
          total: pagination.total || 0,
          perPage: pagination.per_page || 20,
        },
        clientsLoading: false,
      });
    } catch (error: any) {
      set({
        clientsError: error.response?.data?.message || 'Ошибка загрузки клиентов',
        clientsLoading: false,
      });
    }
  },

  createClient: async (data: Partial<Client>) => {
    try {
      const response = await api.post('/clients', data);
      const newClient = response.data;

      // Перезагружаем список после создания
      await get().fetchClients();

      return newClient;
    } catch (error: any) {
      throw error;
    }
  },

  updateClient: async (id: string, data: Partial<Client>) => {
    try {
      const response = await api.put(`/clients/${id}`, data);

      // Перезагружаем список после обновления
      await get().fetchClients();

      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  deleteClient: async (id: string) => {
    try {
      await api.delete(`/clients/${id}`);

      // Перезагружаем список после удаления
      await get().fetchClients();
    } catch (error: any) {
      throw error;
    }
  },

  handleClientSSE: (event) => {
    const { type, data } = event;

    switch (type) {
      case 'client.created':
        set((state) => {
          const clientData = data.client || data.data?.client || data;
          const clientId = clientData.id || clientData._id;

          // Проверяем есть ли уже такой клиент (по id или _id)
          const exists = state.clients.some((c) => {
            const existingId = c.id || c._id;
            return existingId && (
              existingId === clientId ||
              existingId === clientData.id ||
              existingId === clientData._id
            );
          });

          if (!exists && clientId) {
            console.log('[SSE] Adding new client:', clientId);
            return { clients: [clientData, ...state.clients] };
          }

          console.log('[SSE] Client already exists, skipping:', clientId);
          return state;
        });
        break;

      case 'client.updated':
        set((state) => {
          const clientData = data.client || data.data?.client || data;
          const clientId = clientData.id || clientData._id;

          return {
            clients: state.clients.map((c) => {
              const existingId = c.id || c._id;
              return existingId === clientId ? { ...c, ...clientData, id: clientId } : c;
            }),
          };
        });
        break;

      case 'client.deleted':
        set((state) => {
          const clientId = data.id || data.data?.id;
          return {
            clients: state.clients.filter((c) => {
              const existingId = c.id || c._id;
              return existingId !== clientId;
            }),
          };
        });
        break;
    }
  },

  setClientsFilters: (filters: ClientsFilters) => {
    set({
      clientsFilters: filters,
      clientsPagination: { ...get().clientsPagination, page: 1 }, // ✅ Сброс на первую страницу
    });
    get().fetchClients(filters);
  },

  setClientsPage: (page: number) => {
    set((state) => ({
      clientsPagination: { ...state.clientsPagination, page },
    }));
    get().fetchClients();
  },

  optimisticCreateClient: (tempId: string, data: Partial<Client>) => {
    const tempClient: Client = {
      id: tempId,
      name: data.name || '',
      email: data.email,
      phone: data.phone,
      address: data.address,
      _optimistic: true,
    };

    set((state) => ({
      clients: [tempClient, ...state.clients],
      optimisticClients: new Map([...state.optimisticClients, [tempId, tempClient]]),
    }));
  },

  optimisticUpdateClient: (id: string, data: Partial<Client>) => {
    set((state) => {
      const updated = state.clients.map((c) => {
        if (c.id === id || c._id === id) {
          return { ...c, ...data, _optimistic: true };
        }
        return c;
      });

      const client = updated.find((c) => c.id === id || c._id === id);

      return {
        clients: updated,
        optimisticClients: client
          ? new Map([...state.optimisticClients, [id, client]])
          : state.optimisticClients,
      };
    });
  },

  optimisticDeleteClient: (id: string) => {
    set((state) => ({
      clients: state.clients.filter((c) => c.id !== id && c._id !== id),
      optimisticClients: new Map([...state.optimisticClients, [id, null as any]]),
    }));
  },
});
